import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkles, Megaphone, Store, Building2, Wrench, User } from "lucide-react";

const roles = [
  { value: "organizer", label: "Organisateur", desc: "Je crée des événements", icon: Megaphone },
  { value: "exhibitor", label: "Exposant", desc: "Je réserve des stands", icon: Store },
  { value: "venue_owner", label: "Propriétaire de lieu", desc: "Je loue mon espace", icon: Building2 },
  { value: "provider", label: "Prestataire", desc: "Je propose des services", icon: Wrench },
  { value: "visitor", label: "Visiteur", desc: "Je découvre des événements", icon: User },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ name, email, password, role });
      toast.success("Compte créé !");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (role) sessionStorage.setItem("pending_role", role);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] max-w-3xl mx-auto px-6 py-12">
      <div className="mb-8">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="h-8 w-8 bg-primary text-white grid place-items-center rounded-md">
            <Sparkles className="h-4 w-4" />
          </div>
          <span className="font-heading font-light tracking-editorial text-ink">EventMarket.</span>
        </Link>
        <h1 className="font-heading font-light tracking-editorial text-4xl text-ink mb-2">
          Créer un compte
        </h1>
        <p className="text-muted-foreground">
          Étape {step}/2 — {step === 1 ? "Choisissez votre profil" : "Vos informations"}
        </p>
      </div>

      {step === 1 && (
        <div className="space-y-3" data-testid="role-selector">
          {roles.map((r) => {
            const Icon = r.icon;
            return (
              <button
                key={r.value}
                type="button"
                data-testid={`role-${r.value}`}
                onClick={() => { setRole(r.value); setStep(2); }}
                className={`w-full flex items-start gap-4 p-5 text-left border rounded-md transition-all hover:border-accent hover:bg-accent/5 ${
                  role === r.value ? "border-accent bg-accent/5" : "border-ink/10"
                }`}
              >
                <div className="h-10 w-10 bg-primary text-white rounded-md grid place-items-center flex-shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-heading font-medium text-ink">{r.label}</p>
                  <p className="text-sm text-muted-foreground">{r.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {step === 2 && (
        <>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name">Nom complet</Label>
              <Input
                id="name"
                data-testid="register-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jean Dupont"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe (6 caractères minimum)</Label>
              <Input
                id="password"
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="register-error" className="text-sm text-destructive">{error}</p>
            )}
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setStep(1)}>Retour</Button>
              <Button
                type="submit"
                data-testid="register-submit"
                disabled={loading}
                className="flex-1 bg-ink hover:bg-ink/85 text-cream rounded-none"
              >
                {loading ? "Création..." : "Créer mon compte"}
              </Button>
            </div>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-cream-200" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">ou</span>
            <div className="flex-1 h-px bg-cream-200" />
          </div>

          <Button
            type="button"
            data-testid="register-google"
            onClick={handleGoogle}
            variant="outline"
            className="w-full"
          >
            S'inscrire avec Google ({roles.find((r) => r.value === role)?.label})
          </Button>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Déjà un compte ?{" "}
            <Link to="/login" className="text-accent font-semibold hover:underline">
              Se connecter
            </Link>
          </p>
        </>
      )}
    </div>
  );
}
